import { useCallback, useEffect, useMemo, useState } from "react";
import { getUsageSummary } from "../helpers/configRoutes/backend_helper";

export const PLAN_FEATURES = {
  USERS: "users",
  LOADS: "loads",
  TRUCKS: "trucks",
  TRAILERS: "trailers",
  AI_CHAT: "ai_chat",
  AI_LOADS: "ai_loads",
  AI_MAINTENANCE: "ai_maintenance",
};

const emptyFeature = {
  used: 0,
  limit: null,
  available: null,
  allowed: true,
  is_unlimited: true,
  is_blocked: false,
  is_at_limit: false,
  is_over_limit: false,
  over_limit_by: 0,
};

export const usageLimitText = (feature, label = "elementos") => {
  if (!feature) return "";
  if (feature.is_unlimited || feature.limit === null || feature.limit === undefined) {
    return `${feature.used ?? 0} usados / ilimitado`;
  }
  return `${feature.used ?? 0} de ${feature.limit} ${label}`;
};

export const usageBlockedMessage = (feature, label = "elementos") => {
  if (!feature) return "Tu plan actual no permite esta acción.";
  if (feature.allowed) return "";
  if (feature.is_over_limit && feature.limit !== null && feature.limit !== undefined) {
    return `Tu plan actual quedó por debajo del uso actual: tienes ${feature.used ?? 0} ${label} para un límite de ${feature.limit}. Reduce registros o sube de plan para volver a dar de alta.`;
  }
  if (feature.limit !== null && feature.limit !== undefined) {
    return `Llegaste al límite de ${feature.limit} ${label} incluido en tu plan. Sube de plan para continuar.`;
  }
  return "Este servicio no está incluido en tu plan actual.";
};

export const usageStatusTone = (feature) => {
  if (!feature || feature.is_unlimited || feature.limit === null || feature.limit === undefined) {
    return "ok";
  }
  if (feature.is_over_limit) return "danger";
  if (feature.is_at_limit || feature.allowed === false) return "warning";
  return "ok";
};

const normalizeUsage = (payload) => {
  const usage = payload?.usage || {};
  return {
    activeSubscription: usage.active_subscription || null,
    features: usage.features || {},
  };
};

export default function usePlanUsage() {
  const [usage, setUsage] = useState({ activeSubscription: null, features: {} });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const response = await getUsageSummary();
    if (response?.status) {
      setUsage(normalizeUsage(response));
    }
    setLoading(false);
    return response;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const getFeature = useCallback(
    (featureKey) => usage.features?.[featureKey] || emptyFeature,
    [usage.features]
  );

  const canUse = useCallback(
    (featureKey, quantity = 1) => {
      const feature = getFeature(featureKey);
      if (feature.is_unlimited || feature.limit === null || feature.limit === undefined) {
        return true;
      }
      return feature.allowed && (feature.available ?? 0) >= quantity;
    },
    [getFeature]
  );

  const getBlockedFeatures = useCallback(
    () => Object.values(usage.features || {}).filter((feature) => feature?.is_blocked),
    [usage.features]
  );

  const getOverLimitFeatures = useCallback(
    () => Object.values(usage.features || {}).filter((feature) => feature?.is_over_limit),
    [usage.features]
  );

  return useMemo(
    () => ({
      ...usage,
      loading,
      refresh,
      getFeature,
      canUse,
      getBlockedFeatures,
      getOverLimitFeatures,
    }),
    [usage, loading, refresh, getFeature, canUse, getBlockedFeatures, getOverLimitFeatures]
  );
}
